import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import client from '../../../lib/feathers';
import { setVipStatus, setVipLoading, setVipError } from '../../../lib/vipSlice';
import VIPEnrollmentView from './VIPEnrollmentView';
import VIPEnrolledView from './VIPEnrolledView';
import VIPNotEnrolledReadOnlyView from './VIPNotEnrolledReadOnlyView';

export default function VIPPanel({ serviceName = 'industry/listing-vip-status', readOnly = false, basePath } = {}) {
  const dispatch = useDispatch();
  const listing = useSelector((state) => state.listings.selected);
  const vipStatus = useSelector((state) => state.vip.status);
  const loading = useSelector((state) => state.vip.loading);
  const error = useSelector((state) => state.vip.error);

  useEffect(() => {
    if (listing?.id) {
      fetchVIPStatus();
    }
  }, [listing?.id]);

  const fetchVIPStatus = async () => {
    try {
      dispatch(setVipLoading(true));
      const service = client.service(serviceName);
      const result = await service.find({
        query: {
          listingId: listing.id
        }
      });
      dispatch(setVipStatus(result));
    } catch (e) {
      console.error('Error fetching VIP status:', e);
      dispatch(setVipError(e.message || 'Failed to load VIP status'));
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <p className="text-red-800">Error: {error}</p>
        <button
          onClick={fetchVIPStatus}
          className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
        >
          Try again
        </button>
      </div>
    );
  }

  if (!vipStatus) {
    return null;
  }

  // Check if listing is eligible for VIP
  if (!vipStatus.canEnroll && !vipStatus.vipNetworkEnabled && !readOnly) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-md p-6">
        <h3 className="text-lg font-medium text-yellow-900 mb-2">
          VIP Network Not Available
        </h3>
        <p className="text-yellow-700">
          Your listing must be verified to join the VIP Network.
          {vipStatus.listingStatus === 'unverified' && ' Please verify your listing first.'}
        </p>
      </div>
    );
  }

  // Render appropriate view based on enrollment status
  return vipStatus.vipNetworkEnabled ? (
    <VIPEnrolledView
      vipStatus={vipStatus}
      listing={listing}
      onRefresh={fetchVIPStatus}
      readOnly={readOnly}
      basePath={basePath}
    />
  ) : readOnly ? (
    <VIPNotEnrolledReadOnlyView vipStatus={vipStatus} listing={listing} />
  ) : (
    <VIPEnrollmentView vipStatus={vipStatus} listing={listing} onEnrollSuccess={fetchVIPStatus} />
  );
}
